// Playback volume (2026-10-06): one volume for every audio player on this device (Asetukset →
// Ääni), and loudness levelling of our own recordings.
//
// Every <audio> in the app goes through HTMLMediaElement.prototype.play, so the volume is applied
// there once (installPlaybackVolume, from main.tsx) instead of in each player: Peterson's clips,
// the actors' show clips, our recordings, the shadowing loop, sequences, and any player added
// later get it without knowing about it.
//
//   effective gain = device volume × the recording's measured gain (our recordings only; 1 for
//   everything else). The measured gain brings a quiet or loud recording to the common speech
//   level and is capped so its peaks never pass the ceiling (engine audio/trim.ts LOUDNESS), and
//   the device volume is at most 1, so playback never clips.
//
// How it is applied: `el.volume` when the gain is ≤ 1 and the browser honours it. iOS ignores
// `el.volume` (the hardware buttons own it) and nothing can make an element louder than 1, so in
// those cases the element is routed once through a Web Audio GainNode (same-origin audio only: a
// cross-origin file would play silent through Web Audio). If Web Audio is missing or fails, the
// element plays as it is.
import { recordingGain, recordingIdOfUrl } from './audio';
import { preferPlayback } from './audio-session';

const KEY = 'valyrio:volume';
const listeners = new Set<() => void>();

function readStored(): number {
  try {
    const raw = localStorage.getItem(KEY);
    const v = Number(raw);
    return raw !== null && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
  } catch {
    return 1;
  }
}

/** This device's volume, 0…1 (1 until changed). */
export function getVolume(): number {
  return vol();
}

export function setVolume(v: number): void {
  const x = Math.min(1, Math.max(0, Math.round(v * 100) / 100));
  try {
    localStorage.setItem(KEY, String(x));
  } catch {
    /* kept for this page only */
  }
  cached = x;
  for (const l of listeners) l();
  for (const el of playing) apply(el);
}

let cached: number | null = null;
const vol = () => (cached ??= readStored());

// ---------- mute (Pika-asetukset, 2026-10-06) ----------

const UNMUTED_KEY = 'valyrio:volume:unmuted';

/** Mute (volume 0, the level remembered) or bring the remembered level back. */
export function toggleMute(): void {
  const v = vol();
  if (v > 0) {
    try {
      localStorage.setItem(UNMUTED_KEY, String(v));
    } catch {
      /* unmute goes back to full volume */
    }
    setVolume(0);
    return;
  }
  let back = 1;
  try {
    const raw = Number(localStorage.getItem(UNMUTED_KEY));
    if (Number.isFinite(raw) && raw > 0) back = raw;
  } catch {
    /* full volume */
  }
  setVolume(back);
}

/** Tests: forget the remembered value. */
export function __resetVolume(): void {
  cached = null;
}

export function subscribeVolume(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** The gain an element should play at now (device volume × the recording's levelling gain). */
export function gainFor(src: string, volume = vol()): number {
  const id = recordingIdOfUrl(src);
  const g = id ? (recordingGain(id) ?? 1) : 1;
  return Math.max(0, volume * g);
}

// ---------- applying it ----------

type Ctx = AudioContext;
let ctx: Ctx | null = null;
const routed = new WeakMap<HTMLMediaElement, GainNode>();
const playing = new Set<HTMLMediaElement>();
let volumeWorks: boolean | null = null;

/** iOS reports volume 1 whatever is set: the property is read-only there. */
function elementVolumeWorks(): boolean {
  if (volumeWorks !== null) return volumeWorks;
  try {
    const a = document.createElement('audio');
    a.volume = 0.5;
    volumeWorks = Math.abs(a.volume - 0.5) < 0.01;
  } catch {
    volumeWorks = false;
  }
  return volumeWorks;
}

function sameOrigin(src: string): boolean {
  try {
    return new URL(src, location.href).origin === location.origin || src.startsWith('blob:');
  } catch {
    return false;
  }
}

function audioContextClass(): typeof AudioContext | undefined {
  return typeof window !== 'undefined'
    ? (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
    : undefined;
}

function route(el: HTMLMediaElement): GainNode | null {
  const have = routed.get(el);
  if (have) return have;
  const AC = audioContextClass();
  if (!AC) return null;
  try {
    // iOS 17+: let Web Audio play with the ring/silent switch on, like an <audio> element does
    // (never while a recorder holds 'play-and-record': audio-session.ts).
    preferPlayback();
    ctx ??= new AC();
    const node = ctx.createGain();
    ctx.createMediaElementSource(el).connect(node);
    node.connect(ctx.destination);
    routed.set(el, node);
    routedAny = true;
    return node;
  } catch {
    return null;
  }
}

function apply(el: HTMLMediaElement): void {
  const src = el.currentSrc || el.src || '';
  const g = gainFor(src);
  const node = routed.get(el);
  if (node) {
    node.gain.value = g;
    el.volume = 1;
    if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => undefined);
    return;
  }
  if (g <= 1 && elementVolumeWorks()) {
    el.volume = g;
    return;
  }
  if (Math.abs(g - 1) < 0.01) {
    el.volume = 1;
    return;
  }
  if (!sameOrigin(src)) {
    el.volume = Math.min(1, g);
    return;
  }
  const n = route(el);
  if (n) {
    n.gain.value = g;
    el.volume = 1;
    if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => undefined);
  } else {
    el.volume = Math.min(1, g);
  }
}

/** Re-apply to whatever is playing (a gain was just measured). */
export function reapplyVolume(): void {
  for (const el of playing) apply(el);
}

// ---------- iOS: start the context in the first tap ----------

// WebKit starts an AudioContext only inside a user gesture, once per context. The levelling context
// used to be created lazily in route(), and when the first element to need it was started by an
// `ended` handler (Kuuntele kaikki, a scene's lines back to back: together/useSequence.ts,
// formvoices/useSequence.ts) it stayed suspended, and that element, already routed into it, was
// silent. So on iOS (where el.volume is ignored, the one place routing is common) the context is
// created and started in the first tap anywhere; after one start in a gesture WebKit lets the page
// resume it later without one. Nothing is routed yet, so it is suspended again at once. PCs honour
// el.volume and need no unlock: nothing happens there.
const UNLOCK_EVENTS = ['touchend', 'pointerup', 'click', 'keydown'] as const;
let unlocked = false;
/** Whether any element plays through the context yet (then it is never suspended here). */
let routedAny = false;

function unlockOnFirstTap(): void {
  if (typeof window === 'undefined' || elementVolumeWorks()) return;
  const found = audioContextClass();
  if (!found) return;
  const AC: typeof AudioContext = found;
  const done = () => {
    unlocked = true;
    for (const t of UNLOCK_EVENTS) window.removeEventListener(t, onTap, true);
  };
  const started = (c: AudioContext) => {
    done();
    if (!routedAny) void c.suspend().catch(() => undefined);
  };
  function onTap() {
    if (unlocked) return done();
    try {
      ctx ??= new AC();
      const c = ctx;
      if (c.state === 'running') return started(c);
      // a touchend that ended a scroll is no gesture: the listeners stay until a start succeeds
      void c
        .resume()
        .then(() => {
          if (c.state === 'running') started(c);
        })
        .catch(() => undefined);
    } catch {
      done();
    }
  }
  for (const t of UNLOCK_EVENTS) window.addEventListener(t, onTap, { capture: true, passive: true });
}

let installed = false;

/** Patch HTMLMediaElement.play once: every player gets the device volume and the levelling. */
export function installPlaybackVolume(onPlay?: (el: HTMLMediaElement) => void): void {
  if (installed || typeof HTMLMediaElement === 'undefined') return;
  installed = true;
  unlockOnFirstTap();
  const original = HTMLMediaElement.prototype.play;
  HTMLMediaElement.prototype.play = function play(this: HTMLMediaElement) {
    try {
      apply(this);
      if (!playing.has(this)) {
        playing.add(this);
        const done = () => {
          playing.delete(this);
          this.removeEventListener('pause', done);
          this.removeEventListener('ended', done);
        };
        this.addEventListener('pause', done);
        this.addEventListener('ended', done);
      }
      onPlay?.(this);
    } catch {
      /* never stand between the person and the sound */
    }
    return original.call(this);
  };
}
